import type { ChatSourceBrowserTabSnapshot } from "@mirage/mosaics/Chat/types";
import {
  getTabInfos,
  ingestTab,
} from "@mirage/service-extension-tab-ingestion";
import type {
  IngestionResultContent,
  TabInfo,
  TabInfosSnapshot,
} from "@mirage/service-extension-tab-ingestion/service/types";
import { tagged } from "@mirage/service-logging";
import { parseFileInWorker } from "@mirage/shared/worker/file-parser-client";
import { expectExtensionTabIngestion } from "@mirage/webapp/helpers/sentry";
import { useCallback, useEffect, useMemo, useState } from "react";

const logger = tagged("BrowserTabsIngestion");

export function useTabInfos() {
  const [tabInfos, setTabInfos] = useState<TabInfosSnapshot | undefined>(
    undefined,
  );

  const refreshTabInfos = useCallback(() => {
    if (!expectExtensionTabIngestion()) {
      return; // no-op if extension is not installed or not supported
    }
    getTabInfos()
      .then(setTabInfos)
      .catch((error) => {
        logger.error(
          "error getting tab infos, likely due to extension not running",
          error,
        );
      });
  }, []);

  useEffect(() => {
    refreshTabInfos();
  }, [refreshTabInfos]);

  // sort tabs by lastAccessed time
  const sortedTabInfos = useMemo(() => {
    const tabs = [...(tabInfos?.tabs || [])];
    return tabs
      .filter(
        (tab) =>
          tab.url !== undefined && // ignore tabs that don't have a URL
          !location.href.startsWith(tab.url), // ignore tabs that are the current tab (using startsWith as tabInfos may strip hashes)
      )
      .sort((a, b) => b.lastAccessed - a.lastAccessed);
  }, [tabInfos]);

  // group sortedTabInfos by window
  const windowTabInfos = useMemo(() => {
    const windowTabs: Map<number, TabInfo[]> = new Map();
    for (const tabInfo of sortedTabInfos) {
      const windowID = tabInfo.windowID;
      if (!windowTabs.has(windowID)) {
        windowTabs.set(windowID, []);
      }
      windowTabs.get(windowID)?.push(tabInfo);
    }
    return windowTabs;
  }, [sortedTabInfos]);

  return { windowTabInfos, sortedTabInfos, refreshTabInfos };
}

export async function getBrowserTabsAttachments(
  selectedTabIDs: Set<number>,
): Promise<ChatSourceBrowserTabSnapshot[]> {
  const attachments = await Promise.all(
    Array.from(selectedTabIDs).map((tabID) =>
      ingestTabAndExtractContent(tabID),
    ),
  );
  return attachments.filter((attachment) => attachment !== undefined);
}

async function ingestTabAndExtractContent(
  tabID: number,
): Promise<ChatSourceBrowserTabSnapshot | undefined> {
  const ingestionResult = await ingestTab(tabID);
  if (ingestionResult.result.type === "error") {
    logger.info("error ingesting tab", ingestionResult.tabInfo.id);
    return undefined;
  }
  if (ingestionResult.tabInfo.url === undefined) {
    logger.info("tab has no URL", ingestionResult.tabInfo.id);
    return undefined;
  }
  const contentText = await getIngestionResultContentText(
    ingestionResult.result.content,
  );
  if (contentText === undefined) {
    logger.info(
      "failed to extract content from tab",
      ingestionResult.tabInfo.id,
    );
    return undefined;
  }
  return {
    type: "browser_tab_snapshot",
    tabInfo: ingestionResult.tabInfo,
    body: contentText,
  };
}

async function getIngestionResultContentText(
  content: IngestionResultContent,
): Promise<string | undefined> {
  switch (content.type) {
    case "bytes": {
      if (content.mimeType !== "application/pdf") {
        logger.info("non-PDF bytes content type", content.mimeType);
        return undefined; // only PDF is supported for now
      }
      const file = base64ToFile(
        content.bytesBase64,
        content.mimeType,
        `file.pdf`,
      );
      return await parseFileInWorker(file);
    }
    case "text":
      return content.text;
    default: {
      content satisfies never;
      return undefined;
    }
  }
}

function base64ToFile(base64: string, mimeType: string, fileName: string) {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mimeType });
  return new File([blob], fileName, {
    type: mimeType,
  });
}
